@if (selectedCell()) {
  <div class="card shadow-sm mb-2">
    <div class="card-body">
      <!-- Task Info -->
      <div class="card-title">
        <h5>
          @if (selectedCell()!.task.isProductive) {
            {{ selectedCell()!.task.customerName }} -
          }
          {{ selectedCell()!.task.taskName }}
        </h5>
        <small class="text-muted">{{ selectedCell()!.date | date: 'EEEE, d MMMM y' }}</small>
      </div>

      <hr />

      <div class="row mb-3">
        <div class="col-6 col-md-3">
          <label
            for="start"
            class="form-label"
            i18n="
              label|Label for the start time input field in the additional details form@@startLabel"
            >Start</label
          >
          <input
            type="text"
            class="form-control"
            id="start"
            [value]="getSelectedCellValue('startTime')"
            [disabled]="isDisabled()"
            [class.disabled-look]="isDisabled()"
            (blur)="onTimeBlur('startTime', $event)"
            placeholder="--:--"
          />
        </div>
        <div class="col-6 col-md-3">
          <label
            for="break"
            class="form-label"
            i18n="
              label|Label for the break time input field in the additional details form@@breakLabel"
            >Break</label
          >
          <input
            type="text"
            class="form-control"
            id="break"
            [value]="getSelectedCellValue('lunchLength')"
            (blur)="onTimeBlur('lunchLength', $event)"
            [disabled]="isDisabled()"
            [class.disabled-look]="isDisabled()"
            placeholder="--:--"
          />
        </div>
        <div class="col-6 col-md-3">
          <label
            for="endtime"
            class="form-label"
            i18n="
              label|Label for the calculated end time display in the additional details
              form@@endLabel"
            >End</label
          >
          <input
            type="text"
            class="form-control"
            id="endtime"
            [value]="calculateEndTime()"
            [disabled]="isDisabled()"
            [class.disabled-look]="isDisabled()"
            (blur)="onEndTimeBlur($event)"
            placeholder="--:--"
          />
        </div>
        <div class="col-6 col-md-3">
          <label
            for="total"
            class="form-label"
            i18n="
              label|Label for the total hours display in the additional details form@@totalLabel"
            >Total</label
          >
          <p class="form-control-plaintext" id="total">
            {{ getSelectedCellValue('totalHours') }}
          </p>
        </div>
      </div>

      <!-- Location -->
      <div class="mb-3">
        <label
          class="form-label"
          for="location"
          i18n="
            label|Label for the location selection section in the additional details
            form@@locationLabel"
          >Location</label
        >
        <div id="location">
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              [checked]="getSelectedCellValue('workLocation') === WorkLocation.Remote"
              (change)="onExtraInfoChange('workLocation', WorkLocation.Remote)"
              [disabled]="isDisabled()"
              [class.disabled-look]="isDisabled()"
              id="remote"
            />
            <label
              class="form-check-label"
              for="remote"
              i18n="
                option|Label for the remote location option in the additional details
                form@@remoteLabel"
              >Remote</label
            >
          </div>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              [checked]="getSelectedCellValue('workLocation') === WorkLocation.Office"
              (change)="onExtraInfoChange('workLocation', WorkLocation.Office)"
              [disabled]="isDisabled()"
              [class.disabled-look]="isDisabled()"
              id="office"
            />
            <label
              class="form-check-label"
              for="office"
              i18n="
                option|Label for the office or client location option in the additional details
                form@@officeLabel"
              >Office / Client</label
            >
          </div>
          <div class="form-check form-check-inline">
            <input
              class="form-check-input"
              type="radio"
              [checked]="getSelectedCellValue('workLocation') === WorkLocation.Mixed"
              (change)="onExtraInfoChange('workLocation', WorkLocation.Mixed)"
              [disabled]="isDisabled()"
              [class.disabled-look]="isDisabled()"
              id="mixed"
            />
            <label
              class="form-check-label"
              for="mixed"
              i18n="
                option|Label for the mixed location option in the additional details
                form@@mixedLabel"
              >Mixed</label
            >
          </div>
        </div>
      </div>

      <!-- Description -->
      <div class="mb-3">
        <label
          for="description"
          class="form-label"
          [class.required]="isTrainingTask()"
          i18n="
            label|Label for the description textarea in the additional details
            form@@descriptionLabel"
        >
          Description
        </label>
        <textarea
          #descriptionInput
          class="form-control"
          [class.is-invalid]="isTrainingTask() && !hasValidDescription()"
          id="description"
          rows="3"
          [value]="getSelectedCellValue('description')"
          [placeholder]="descriptionPlaceholder()"
          (input)="onExtraInfoChange('description', descriptionInput.value)"
          [disabled]="isDisabled()"
          [class.disabled-look]="isDisabled()"
          maxlength="128"
        ></textarea>
        @if (isTrainingTask() && !hasValidDescription()) {
          <div class="invalid-feedback d-block">
            <i class="bi bi-exclamation-circle me-1"></i>
            <span
              i18n="
                validation error|Error message displayed when description is too short for training
                tasks@@descriptionMinLengthError"
            >
              Description must be at least {{ TRAINING_DESCRIPTION_MIN_LENGTH }} characters for
              training entries
            </span>
          </div>
        }
      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end gap-2">
        <button
          class="btn btn-danger btn-sm"
          type="button"
          (click)="removeEntryFromChangeTracker()"
          ngbTooltip="Remove this entry from changes"
          i18n-ngbTooltip="
            tooltip|Tooltip explaining the function of the remove entry
            button@@removeEntryButtonTitle"
          [disabled]="isDisabled() || removeEntryFromChangeTrackerDisabled()"
        >
          <i class="bi bi-trash me-1"></i>
          <span
            i18n="
              button|Button label to remove the current timesheet entry from tracked
              updates@@removeEntryButtonLabel"
          >
            Remove entry from updates
          </span>
        </button>
      </div>
    </div>
  </div>
} @else {
  <div class="card shadow-sm">
    <div class="card-body text-center text-muted py-5">
      <i class="bi bi-hand-index fs-1 mb-3 d-block"></i>
      <p
        class="mb-0"
        i18n="
          message|Prompt message displayed when no cell is selected in the timesheet
          view@@selectCellPrompt"
      >
        Select a cell to view and edit additional details
      </p>
    </div>
  </div>
}
